import proj4 from "proj4";
import wellknown from "wellknown";

proj4.defs(
  "EPSG:28992",
  "+title=Amersfoort / RD New +proj=sterea +lat_0=52.15616055555555 +lon_0=5.38763888888889 +k=0.999908  +x_0=155000 +y_0=463000 +ellps=bessel +units=m +towgs84=565.2369,50.0087,465.658,-0.406857330322398,0.350732676542563,-1.8703473836068,4.0812 +no_defs"
);
proj4.defs(
  "EPSG:31370",
  "+proj=lcc +lat_1=51.16666723333333 +lat_2=49.8333339 +lat_0=90 +lon_0=4.367486666666666 +x_0=150000.013 +y_0=5400088.438 +ellps=intl +towgs84=-106.869,52.2978,-103.724,0.3366,-0.457,1.8422,-1.2747 +units=m +no_defs"
);
proj4.defs("EPSG:25832", "+proj=utm +zone=32 +ellps=GRS80 +units=m +no_defs");
proj4.defs(
  "EPSG:2154",
  "+proj=lcc +lat_1=49 +lat_2=44 +lat_0=46.5 +lon_0=3 +x_0=700000 +y_0=6600000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs"
);
proj4.defs(
  "EPSG:2169",
  "+proj=tmerc +lat_0=49.83333333333334 +lon_0=6.166666666666667 +k=1 +x_0=80000 +y_0=100000 +ellps=intl +towgs84=-189.681,18.3463,-42.7695,-0.33746,-3.09264,2.53861,0.4598 +units=m +no_defs"
);
export { proj4 };

export const getBBOX = (wkt) => {
  const ret = [null, null, null, null];
  wkt.match(/([\d.]+ [\d.]+)/g).forEach((e) => {
    const xy = e.split(" "),
      x = parseFloat(xy[0]),
      y = parseFloat(xy[1]);
    if (ret[0] === null || x < ret[0]) ret[0] = x;
    if (ret[1] === null || y < ret[1]) ret[1] = y;
    if (ret[2] === null || x > ret[2]) ret[2] = x;
    if (ret[3] === null || y > ret[3]) ret[3] = y;
  });
  return ret;
};

export const toWKT = (obj, proj?) => {
  if (!obj && obj.coordinates) return null;
  if (proj) {
    const p = proj4("EPSG:" + proj);
    obj = { type: obj.type, coordinates: obj.coordinates.map((c) => c.map((d) => p.forward(d))) };
  }
  return wellknown.stringify(obj);
};

export const fromWkt = (obj) => {
  if (obj.feature) {
    return obj.feature;
  }
  const geoJson = wellknown.parse(obj.geometry);
  if (geoJson) {
    geoJson.properties = obj;
    obj.feature = geoJson;
    return geoJson;
  }
};

export type GPS = [number, number];
export const toXY = (gps: GPS) => gps && (proj4("EPSG:28992").forward(gps) as GPS);
export const emptyFeatureCollection: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
  type: "FeatureCollection",
  features: [],
};

// Shoelace formula on a single ring of [x, y] coordinates in meters.
const ringArea = (ring: number[][]) => {
  let sum = 0;
  for (let i = 0, n = ring.length; i < n; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[(i + 1) % n];
    sum += x1 * y2 - x2 * y1;
  }
  return Math.abs(sum) / 2;
};

// Area in square meters. Accepts a WKT string or a GeoJSON geometry.
// Coordinates are assumed to be lon/lat (EPSG:4326) and are transformed to
// EPSG:28992 (RD New, metric) before the area is calculated.
export const calculateArea = (p): number => {
  if (!p) return 0;
  let geom = typeof p === "string" ? wellknown.parse(p) : p;
  // Unwrap a GeoJSON Feature to its geometry.
  if (geom && geom.type === "Feature") geom = geom.geometry;
  if (!geom) return 0;

  const proj = proj4("EPSG:28992");
  const polygonArea = (polygon: number[][][]) =>
    polygon.reduce((acc, ring, i) => {
      const area = ringArea(ring.map((c) => proj.forward(c) as number[]));
      // First ring is the exterior, subsequent rings are holes.
      return i === 0 ? acc + area : acc - area;
    }, 0);

  if (geom.type === "Polygon") {
    return polygonArea(geom.coordinates as number[][][]);
  }
  if (geom.type === "MultiPolygon") {
    return (geom.coordinates as number[][][][]).reduce((acc, poly) => acc + polygonArea(poly), 0);
  }
  return 0;
};
